import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import {
  Rarity,
  SortingOption,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Shards } from '@sorare/core/src/atoms/icons/Shards';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BrandM, LabelL } from '@sorare/core/src/atoms/typography';
import { TransactionEntry } from '@sorare/core/src/components/mySorare/TransactionsHistory/common/TransactionEntry';
import { EmptyContent } from '@sorare/core/src/contexts/intl/EmptyContent';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import { glossary } from '@sorare/core/src/lib/glossary';

import { SelectRarity } from 'atoms/SelectRarity';
import { SelectSort } from 'atoms/SelectSort';
import { SelectSport } from 'atoms/SelectSport';

import type {
  EssenceTransactionsQuery,
  EssenceTransactionsQueryVariables,
} from './__generated__/index.graphql';

const messages = defineMessages({
  empty: {
    id: 'TransactionsEssenceHistory.empty',
    defaultMessage: 'No Essence Transactions',
  },
});

const QUERY = gql`
  query EssenceTransactionsQuery(
    $rarity: Rarity!
    $sport: Sport!
    $after: String
    $orderBy: SortingOption!
  ) {
    currentUser {
      slug
      cardShardsHistoryTransactions(
        sport: $sport
        rarity: $rarity
        after: $after
        orderBy: $orderBy
      ) {
        nodes {
          id
          date
          totalQuantity
          label
          logoUrl
          description
          quantityByFlavour {
            displayName
            logoUrl
            slug
            count
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
` as TypedDocumentNode<
  EssenceTransactionsQuery,
  EssenceTransactionsQueryVariables
>;

const EssenceAmount = ({
  amount,
  rarity,
}: {
  amount: number;
  rarity: Rarity;
}) => {
  return (
    <Horizontal gap={0.5}>
      <LabelL
        bold
        color={
          amount >= 0 ? 'var(--c-onBase-success)' : 'var(--c-onBase-error)'
        }
      >
        {amount}
      </LabelL>

      <Shards color={getRarityColor(rarity)} />
    </Horizontal>
  );
};

export const EssenceTransactions = () => {
  const {
    flags: { useCommonEssenceTransactions = false },
  } = useFeatureFlags();
  const { sport: defaultSport } = useSportContext();
  const [rarity, setRarity] = useState<Rarity>(Rarity.limited);
  const [sport, setSport] = useState<Sport>(defaultSport);
  const [orderBy, setOrderBy] = useState<SortingOption>(SortingOption.DESC);

  const { data, loading, loadMore } = usePaginatedQuery(QUERY, {
    variables: { rarity, sport, orderBy },
    connection: 'HistoryTransactionInterfaceConnection',
  });
  const paginatedResults = data?.currentUser?.cardShardsHistoryTransactions;

  return (
    <Vertical gap={2}>
      <SBHorizontal>
        <BrandM>
          <FormattedMessage id="myActivity" defaultMessage="My activity" />
        </BrandM>
        <Horizontal>
          <SelectSport value={sport} onChange={setSport} />
          <SelectRarity
            selectedRarity={rarity}
            rarities={
              useCommonEssenceTransactions
                ? [
                    Rarity.common,
                    Rarity.limited,
                    Rarity.rare,
                    Rarity.super_rare,
                    Rarity.unique,
                  ]
                : [
                    Rarity.limited,
                    Rarity.rare,
                    Rarity.super_rare,
                    Rarity.unique,
                  ]
            }
            onChange={setRarity}
          />
          <SelectSort value={orderBy} onChange={setOrderBy} />
        </Horizontal>
      </SBHorizontal>

      <div>
        {paginatedResults?.nodes?.map(transaction => (
          <TransactionEntry
            key={transaction.id}
            title={transaction.label}
            date={transaction.date}
            subtitle={
              transaction.logoUrl ? (
                <>
                  <img
                    src={transaction.logoUrl}
                    alt={transaction.label}
                    height={24}
                  />{' '}
                  {transaction.description}
                </>
              ) : (
                transaction.description
              )
            }
            amount={
              <EssenceAmount
                amount={transaction.totalQuantity}
                rarity={rarity}
              />
            }
          >
            <div style={{ paddingRight: 'var(--double-unit)' }}>
              {transaction.quantityByFlavour.map(quantity => (
                <SBHorizontal key={quantity.slug}>
                  <Horizontal>
                    {!!quantity.logoUrl && (
                      <img
                        src={quantity.logoUrl}
                        alt={quantity.displayName}
                        height={24}
                      />
                    )}
                    {quantity.displayName}
                  </Horizontal>

                  <EssenceAmount amount={quantity.count} rarity={rarity} />
                </SBHorizontal>
              ))}
            </div>
          </TransactionEntry>
        ))}
      </div>

      {paginatedResults?.nodes?.length === 0 && !loading && (
        <EmptyContent message={messages.empty} />
      )}

      {!!paginatedResults?.pageInfo?.hasNextPage &&
        !!paginatedResults?.pageInfo?.endCursor && (
          <Button
            size="small"
            onClick={async () =>
              loadMore(false, {
                after: paginatedResults.pageInfo.endCursor,
              })
            }
            disabled={loading}
          >
            <FormattedMessage {...glossary.showMore} />
          </Button>
        )}
    </Vertical>
  );
};
